import { Logger } from '../../utils/Logger';
import { Entity } from './Entity';
import { Component } from './Component';
import { ComponentManager } from './ComponentManager';
import { EntityManager } from './EntityManager';

/**
 * System class represents a system in the ECS architecture.
 * Systems operate on entities that have specific components.
 */
export abstract class System {
  protected componentManager: ComponentManager;
  protected entityManager: EntityManager;
  protected logger: Logger;
  protected requiredComponents: Set<Function>;
  protected entities: Entity[];

  constructor() {
    this.componentManager = ComponentManager.getInstance();
    this.entityManager = EntityManager.getInstance();
    this.logger = Logger.getInstance();
    this.requiredComponents = new Set();
    this.entities = [];
  }

  /**
   * Register a component type that this system requires
   */
  protected registerRequiredComponent(componentType: Function): void {
    this.requiredComponents.add(componentType);
  }

  /**
   * Check if an entity has all required components
   */
  protected hasRequiredComponents(entity: Entity): boolean {
    for (const componentType of Array.from(this.requiredComponents)) {
      if (!this.componentManager.hasComponent(entity, componentType)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Add an entity to this system
   */
  public addEntity(entity: Entity): void {
    if (this.hasRequiredComponents(entity) && !this.entities.includes(entity)) {
      this.entities.push(entity);
      this.onEntityAdded(entity);
    }
  }

  /**
   * Remove an entity from this system
   */
  public removeEntity(entity: Entity): void {
    const index = this.entities.indexOf(entity);
    if (index !== -1) {
      this.entities.splice(index, 1);
      this.onEntityRemoved(entity);
    }
  }

  /**
   * Remove all entities from this system
   */
  public removeAllEntities(): void {
    const entitiesToRemove = [...this.entities];
    for (const entity of entitiesToRemove) {
      this.removeEntity(entity);
    }
  }

  /**
   * Check if this system can process the given entity
   */
  public canProcessEntity(entity: Entity): boolean {
    return this.hasRequiredComponents(entity);
  }

  /**
   * Get the required component types for this system
   */
  public getRequiredComponents(): Set<Function> {
    return new Set(this.requiredComponents);
  }

  /**
   * Get all entities managed by this system
   */
  public getEntities(): Entity[] {
    return this.entities;
  }

  /**
   * Called when an entity is added to the system
   */
  protected onEntityAdded(entity: Entity): void {}

  /**
   * Called when an entity is removed from the system
   */
  protected onEntityRemoved(entity: Entity): void {}

  /**
   * Update method that systems must implement
   */
  public abstract update(deltaTime: number): void;
}
